import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeSlash, SpinnerGap } from '@phosphor-icons/react';

const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

/**
 * Uncontrolled login inputs — browser autofill writes to the DOM without React
 * onChange. Controlled value={} often leaves password empty on the first submit
 * (looks filled, API gets wrong/empty credentials → "Invalid credentials").
 * Reading FormData/DOM on submit is the reliable fix.
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const formRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const submittingRef = useRef(false);

  const readCredentials = (form) => {
    const root = form || formRef.current;
    const emailEl = emailRef.current || root?.elements?.namedItem?.('email');
    const passEl = passwordRef.current || root?.elements?.namedItem?.('password');
    const fd = root ? new FormData(root) : null;
    const email = String(
      (fd && fd.get('email')) || emailEl?.value || '',
    ).trim().toLowerCase();
    const password = String(
      (fd && fd.get('password')) || passEl?.value || '',
    );
    return { email, password };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setError('');
    setLoading(true);

    // Prefer the submit event target so we always read the live DOM (autofill-safe)
    const { email, password } = readCredentials(e.currentTarget || formRef.current);

    if (!email || !password) {
      setError('Please enter your email and password.');
      setLoading(false);
      submittingRef.current = false;
      return;
    }

    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 422) {
        setError(formatApiError(detail) || 'Please check your email and password format.');
      } else {
        setError(formatApiError(detail) || err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1761555488129-40161d6ea01e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNvcnBvcmF0ZSUyMGFyY2hpdGVjdHVyZSUyMGxpZ2h0fGVufDB8fHx8MTc3NTA3NTk1MXww&ixlib=rb-4.1.0&q=85"
          alt="Corporate architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/intellectt_logo.png" alt="Intellectt" className="w-72 object-contain" />
          </div>
          <h1 className="text-4xl font-bold text-white text-center mb-4 font-['Outfit']">
            Enterprise Incentive Portal
          </h1>
          <p className="text-slate-300 text-center text-lg max-w-md">
            Manage sales placements, calculate incentives, and streamline your approval workflow.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <img src="/intellectt_logo_dark.png" alt="Intellectt" className="h-10 object-contain" />
          </div>

          <div className="w-full">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-2xl font-['Outfit']">Welcome back</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  ref={formRef}
                  onSubmit={handleLogin}
                  className="space-y-4"
                  autoComplete="on"
                  noValidate
                >
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm" data-testid="login-error">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      ref={emailRef}
                      id="login-email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      placeholder="you@intellectt.com"
                      defaultValue=""
                      required
                      data-testid="login-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        ref={passwordRef}
                        id="login-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        defaultValue=""
                        required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => {
                          const el = passwordRef.current;
                          const current = el?.value || '';
                          setShowPassword((v) => !v);
                          // Preserve autofilled/uncontrolled value across type toggle
                          requestAnimationFrame(() => {
                            if (passwordRef.current && passwordRef.current.value !== current) {
                              passwordRef.current.value = current;
                            }
                          });
                        }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <SpinnerGap size={20} className="animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
